<template>
  <div class="order-timeline-page content-section">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h1 class="mb-0">Order Timeline #{{ orderId }}</h1>
      <a
        class="btn btn-outline-secondary btn-sm"
        :href="legacyProcessUrl"
        target="_blank"
        rel="noopener"
      >Open order</a>
    </div>

    <div class="filters">
      <div class="row g-3">
        <div class="col-md-4">
          <FormKit
            type="datepicker"
            label="From"
            v-model="selectedFrom"
            format="DD/MM/YYYY"
            timezone="Europe/London"
            clearable
          />
        </div>
        <div class="col-md-4">
          <FormKit
            type="datepicker"
            label="To"
            v-model="selectedTo"
            format="DD/MM/YYYY"
            timezone="Europe/London"
            clearable
          />
        </div>
        <div class="col-md-4">
          <label class="form-label">Event types</label>
          <select class="form-select" multiple v-model="selectedTypes" size="6">
            <option v-for="type in allTypes" :key="type" :value="type">{{ type }}</option>
          </select>
        </div>
      </div>

      <div class="action-bar mt-3">
        <button type="button" @click="clearFilters">Clear filters</button>
        <button type="button" @click="reload">Apply filters</button>
      </div>
    </div>

    <div v-if="error" class="alert alert-danger mt-3" role="alert">{{ error }}</div>
    <div
      v-if="!projectionComplete && !error"
      class="alert alert-info mt-3"
      role="status"
    >This order's timeline is still being built. Showing events available so far.</div>
    <div v-if="loading && items.length === 0" class="mt-3">Loading timeline…</div>

    <div class="timeline-table-wrapper mt-4" v-if="items.length">
      <table class="timeline-table">
        <thead>
          <tr>
            <th>Type</th>
            <th>What</th>
            <th>When</th>
            <th>Action</th>
            <th>User</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="(item, index) in items" :key="item.id">
            <tr
              v-if="index === 0 || displayDate(item) !== displayDate(items[index - 1])"
              class="timeline-day"
            >
              <td colspan="5">{{ displayDate(item) }}</td>
            </tr>
            <tr class="timeline-entry" :class="cssClass(item.type)">
              <td>{{ item.type }}</td>
              <td>{{ item.summary }}</td>
              <td>{{ formatTime(item.occurred_at) }}</td>
              <td>
                {{ item.action }}
                <a
                  v-if="item.metadata && item.metadata.maps_query"
                  class="timeline-map-link"
                  :href="'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(item.metadata.maps_query)"
                  target="_blank"
                  rel="noopener"
                >View location</a>
              </td>
              <td>{{ item.user }}</td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <div v-else-if="!loading && !error" class="mt-4 text-muted">No timeline events found.</div>

    <div class="action-bar mt-4" v-if="showPagination">
      <button type="button" :disabled="loading || pageNumber <= 1" @click="goPrevious">
        Previous
      </button>
      <span class="page-label">Page {{ pageNumber }}</span>
      <button type="button" :disabled="loading || !hasMore" @click="goNext">
        {{ loading ? 'Loading…' : 'Next' }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import OrderService from '@/services/OrderService'
import { formatDateTimeEN } from '@/utils/dateUtils'

const route = useRoute()
const orderId = computed(() => route.params.id)

const allTypes = [
  'Order Status',
  'Order Details',
  'Item Status',
  'Garment',
  'Dispatch Rail',
  'Driver scan',
  'Quality Control',
  'Piecing Up',
  'Processing RFID',
  'Barcode Scan',
  'RFID Scan',
  'Logistics Tracking',
  'Log Order Status',
]

const items = ref([])
const nextCursor = ref('')
const hasMore = ref(false)
const loading = ref(false)
const error = ref('')
const projectionComplete = ref(true)
const selectedTypes = ref([])
const selectedFrom = ref(null)
const selectedTo = ref(null)

// cursorStack holds the cursor used to fetch each page after page 1.
// Empty stack => page 1; pageNumber = cursorStack.length + 1.
const cursorStack = ref([])

// Filters committed to the server for the current result set. Prev/Next must reuse
// these (not the live form) so every page is filtered identically and matches the cursor.
const appliedFilters = ref({ types: [], from: undefined, to: undefined })

const pageNumber = computed(() => cursorStack.value.length + 1)
const showPagination = computed(() => pageNumber.value > 1 || hasMore.value)

const legacyProcessUrl = computed(() => {
  return (process.env.VUE_APP_EPOS_API_BASE || '') +
    '?page=order&mode=process&order_id=' + orderId.value
})

const cssClass = (type) => String(type || '').toLowerCase().replace(/\s+/g, '')
// Match legacy order-timeline displayDate (l jS F Y), including the year.
const displayDate = (item) => new Date(item.occurred_at).toLocaleDateString('en-GB', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
})
const formatTime = (value) => formatDateTimeEN(value)

// dayBoundaryISO converts a datepicker value to an inclusive day boundary in ISO form,
// returning undefined for empty/invalid input so a bad value can never drop the filter silently.
const dayBoundaryISO = (value, endOfDay) => {
  if (!value) return undefined
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return undefined
  d.setHours(endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0, endOfDay ? 999 : 0)
  return d.toISOString()
}

// commitFilters snapshots the live form into the filters sent to the server.
const commitFilters = () => {
  appliedFilters.value = {
    types: [...selectedTypes.value],
    from: dayBoundaryISO(selectedFrom.value, false),
    to: dayBoundaryISO(selectedTo.value, true),
  }
}

// Page size for the timeline API. Lower temporarily (e.g. to 10) to test multi-page nav.
const buildOptions = (cursor = '') => ({
  limit: 1000,
  cursor: cursor || undefined,
  types: appliedFilters.value.types,
  from: appliedFilters.value.from,
  to: appliedFilters.value.to,
})

const fetchPage = async ({ cursor = '', resetFilters = false } = {}) => {
  loading.value = true
  error.value = ''
  try {
    if (resetFilters) {
      commitFilters()
    }
    const response = await OrderService.getOrderTimeline(
      orderId.value,
      buildOptions(cursor)
    )
    const data = response.data?.data || {}
    items.value = data.items || []
    nextCursor.value = data.next_cursor || ''
    hasMore.value = !!data.has_more
    const projection = data.projection || {}
    projectionComplete.value = projection.complete !== false
  } catch (err) {
    error.value = err?.response?.data?.message || 'Failed to load order timeline'
    items.value = []
    hasMore.value = false
    nextCursor.value = ''
  } finally {
    loading.value = false
  }
}

const reload = async () => {
  cursorStack.value = []
  nextCursor.value = ''
  await fetchPage({ resetFilters: true })
}

const goNext = async () => {
  if (!hasMore.value || loading.value || !nextCursor.value) return
  const cursor = nextCursor.value
  const previousStack = cursorStack.value
  cursorStack.value = [...previousStack, cursor]
  try {
    await fetchPage({ cursor })
    if (error.value) {
      cursorStack.value = previousStack
    }
  } catch (err) {
    cursorStack.value = previousStack
    throw err
  }
}

const goPrevious = async () => {
  if (pageNumber.value <= 1 || loading.value) return
  const previousStack = cursorStack.value
  const nextStack = previousStack.slice(0, -1)
  cursorStack.value = nextStack
  const cursor = nextStack.length ? nextStack[nextStack.length - 1] : ''
  try {
    await fetchPage({ cursor })
    if (error.value) {
      cursorStack.value = previousStack
    }
  } catch (err) {
    cursorStack.value = previousStack
    throw err
  }
}

const clearFilters = () => {
  selectedTypes.value = []
  selectedFrom.value = null
  selectedTo.value = null
  reload()
}

onMounted(reload)
</script>

<style scoped>
.order-timeline-page {
  width: 100%;
}

.timeline-table-wrapper {
  width: 100%;
  overflow-x: auto;
}

.timeline-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.timeline-table thead tr {
  border-bottom: 3px solid #000;
}

.timeline-table th {
  height: 20px;
  padding: 0 6px;
  text-align: left;
  white-space: nowrap;
}

.timeline-table td {
  padding: 4px 6px;
  border: 1px solid rgba(0, 0, 0, 0.3);
  line-height: 1.25;
  white-space: nowrap;
  vertical-align: top;
}

.timeline-table .timeline-day {
  color: #fff;
  background: #2b647b;
  font-weight: 700;
}

.timeline-table .timeline-day td {
  border-color: #2b647b;
}

.timeline-entry.orderstatus {
  background: #55efc4;
  border-left: 5px solid #00b894;
}

.timeline-entry.itemstatus {
  background: #ffeaa7;
  border-left: 5px solid #fdcb6e;
}

.timeline-entry.dispatchrail {
  background: #fab1a0;
  border-left: 5px solid #e17055;
}

.timeline-entry.barcodescan,
.timeline-entry.processingrfid,
.timeline-entry.rfidscan {
  background: #74b9ff;
  border-left: 5px solid #0984e3;
}

.timeline-entry.garment {
  background: #a29bfe;
  border-left: 5px solid #6c5ce7;
}

.timeline-entry.orderdetails {
  background: #fd79a8;
  border-left: 5px solid #e84393;
}

.timeline-entry.qualitycontrol {
  background: #81ecec;
  border-left: 5px solid #00cec9;
}

.timeline-entry.piecingup {
  background: #ff7675;
  border-left: 5px solid #d63031;
}

.timeline-map-link {
  margin-left: 0.5rem;
  white-space: nowrap;
}

.action-bar button + button,
.action-bar button + .page-label,
.action-bar .page-label + button {
  margin-left: 0.5rem;
}

.page-label {
  display: inline-block;
  min-width: 4.5rem;
  text-align: center;
  vertical-align: middle;
}
</style>
